import {
  createRouter,
  createWebHistory,
  type NavigationGuardNext,
  type RouteLocationNormalizedGeneric,
  type RouteLocationNormalizedLoadedGeneric
} from 'vue-router'
import { useStore } from '@/stores'
import { auth } from '@/router/middlewares/auth'
import { initialize } from '@/router/middlewares/initialize'
import { athleticsGate } from '@/router/middlewares/athletics'
import { childcareGate } from '@/router/middlewares/childcare'
import { API } from '@/modules/api'

const MODE_ENV = import.meta.env.VITE_MODE_ENV
const LOBBY_URL = import.meta.env.VITE_LOBBY_URL

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: to => {
        // In development, allow testing by going to a test landing page
        // In production, redirect to lobby
        if (MODE_ENV === 'production') {
          window.location.href = LOBBY_URL
          return '/landing-lobby/dashboard' // fallback
        } else {
          // In development, preserve any existing query params (like real SSO payload)
          // or add a test one if none exists
          const existingSsoPayload =
            to.query.sso_payload || new URLSearchParams(window.location.search).get('sso_payload')

          if (existingSsoPayload) {
            // Preserve the real SSO payload using proper query object
            return {
              path: '/landing-lobby/dashboard',
              query: { sso_payload: existingSsoPayload }
            }
          } else {
            // Add test payload for development
            return {
              path: '/landing-lobby/dashboard',
              query: { sso_payload: 'test123' }
            }
          }
        }
      }
    },
    {
      path: '/quickpay/:guid',
      name: 'QuickPay',
      component: () => import('@/views/QuickpayView.vue') // Lazy loaded chunk
    },
    {
      path: '/landing-lobby/:targetRoute*',
      name: 'LandingLobby',
      component: () => import('@/views/LobbyLandingView.vue'), // Lazy loaded chunk
      beforeEnter: auth
    },
    {
      path: '/landing-powerschool/:targetRoute*',
      name: 'LandingPowerschool',
      component: () => import('@/views/PowerschoolLandingView.vue'), // Lazy loaded chunk
      beforeEnter: auth
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: () => import('@/views/DashboardView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/documents',
      name: 'Documents',
      component: () => import('@/views/DocumentView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/autoPaymentSettings',
      name: 'AutoPaymentSettings',
      component: () => import('@/views/AutoPaymentSettingsView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/orderHistory',
      name: 'OrderHistory',
      component: () => import('@/views/OrderHistoryView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/invoiceHistory',
      name: 'InvoiceHistory',
      component: () => import('@/views/InvoiceHistoryView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/receipt/:orderId',
      name: 'receipt',
      component: () => import('@/views/ReceiptView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/billing',
      name: 'Billing',
      component: () => import('@/views/BillingView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/invoice/:invoiceHeaderId',
      name: 'Invoice',
      component: () => import('@/views/InvoiceView.vue'),
      beforeEnter: initialize
    },
    {
      path: '/nutrition',
      name: 'nutrition',
      component: () => import('@/views/NutritionView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/notificationSettings/:familyAccountId',
      name: 'notificationSettings',
      component: () => import('@/views/NotificationSettingsView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/autoRefill/:familyAccountId',
      name: 'autoRefill',
      component: () => import('@/views/AutoRefillView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/lowBalanceNoticeLevel/:familyAccountId',
      name: 'lowBalanceNoticeLevel',
      component: () => import('@/views/LowBalanceNoticeLevel.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/transactions/family/:familyAccountId',
      name: 'familyTransactions',
      component: () => import('@/views/TransactionView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/transactions/student/:studentAccountId',
      name: 'studentTransactions',
      component: () => import('@/views/TransactionView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/studentSettings/student/:studentAccountId/:familyAccountId',
      name: 'studentSettingsView',
      component: () => import('@/views/StudentSettingsView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/accountSettings',
      name: 'accountSettings',
      component: () => import('@/views/AccountSettingsView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/aiAssistant',
      name: 'aiAssistant',
      component: () => import('@/views/AiAssistantView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/accessibilityStatement',
      name: 'accessibilityStatement',
      component: () => import('@/views/AccessibilityStatementView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/accountSettings/edit/address',
      name: 'editAddress',
      component: () => import('@/views/AccountSettingsAddressView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/accountSettings/edit/phone',
      name: 'editPhone',
      component: () => import('@/views/AccountSettingsPhoneView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/accountSettings/edit/email',
      name: 'editEmail',
      component: () => import('@/views/AccountSettingsEmail.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/accountSettings/edit/password',
      name: 'editPassword',
      component: () => import('@/views/AccountSettingsPasswordView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/accountSettings/edit/securityQuestion',
      name: 'editSecurityQuestion',
      component: () => import('@/views/AccountSettingsSecurityQuestionView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/accountSettings/edit/fundingSources',
      name: 'editFundingSources',
      component: () => import('@/views/AccountSettingsFundingSourcesView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/mealList/family/:familyAccountId',
      name: 'familyMealList',
      component: () => import('@/views/MealListView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/mealList/student/:studentAccountId/:familyAccountId',
      name: 'studentMealList',
      component: () => import('@/views/MealListView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/schoolFees',
      name: 'schoolFees',
      component: () => import('@/views/SchoolFeesView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    {
      path: '/enrollment',
      name: 'enrollment',
      component: () => import('@/views/EnrollmentView.vue'), // Lazy loaded chunk
      beforeEnter: initialize
    },
    // DTA-1092: the Childcare app (childcare enrollments, days and contracts)
    {
      path: '/childcare',
      name: 'childcare',
      component: () => import('@/views/ChildcareView.vue'), // Lazy loaded chunk
      beforeEnter: childcareGate // DTA-1092 release gate (runs initialize)
    },
    // DTA-1092: Childcare › Manage Student
    {
      path: '/childcare/students/:studentAccountId',
      name: 'childcareStudent',
      component: () => import('@/views/ChildcareStudentView.vue'), // Lazy loaded chunk
      beforeEnter: childcareGate // DTA-1092 release gate (runs initialize)
    },
    {
      path: '/athletics',
      name: 'athletics',
      component: () => import('@/views/AthleticsView.vue'), // Lazy loaded chunk
      beforeEnter: athleticsGate // DTA-949 release gate (runs initialize)
    },
    // DTA-799: student-first athletics screens
    {
      path: '/athletics/students/:studentAccountId',
      name: 'athleticsStudent',
      component: () => import('@/views/AthleticsStudentView.vue'), // Lazy loaded chunk
      beforeEnter: athleticsGate // DTA-949 release gate (runs initialize)
    },
    // DTA-949: a student's coupon book sales (entering books sold, invitations)
    {
      path: '/fundraisers/:districtId/books/:bookId/students/:studentId',
      name: 'couponBookSales',
      component: () => import('@/views/CouponBookSalesView.vue'), // Lazy loaded chunk
      beforeEnter: athleticsGate // DTA-949 release gate (runs initialize)
    },
    {
      path: '/athletics/students/:studentAccountId/register',
      name: 'athleticsRegister',
      component: () => import('@/views/AthleticsRegisterView.vue'), // Lazy loaded chunk
      beforeEnter: athleticsGate // DTA-949 release gate (runs initialize)
    },
    // Standalone funding source page (no initialize middleware, no app shell)
    {
      path: '/standalone/add-funding-source',
      name: 'StandaloneAddFundingSource',
      component: () => import('@/views/standalone/StandaloneAddFundingSourceView.vue'),
      meta: { standalone: true }
    },
    // DTA-949 public coupon book pages — NO login, no app shell. The book's
    // private link is the credential for the first; the business location's
    // public key plus its PIN for the second.
    {
      path: '/standalone/coupon-book/:token',
      name: 'StandaloneCouponBook',
      component: () => import('@/views/standalone/CouponBookView.vue'),
      meta: { standalone: true }
    },
    {
      path: '/standalone/coupon-book-stop/:token',
      name: 'StandaloneCouponBookStop',
      component: () => import('@/views/standalone/CouponBookStopView.vue'),
      meta: { standalone: true }
    },
    {
      path: '/standalone/coupon-redeem-scan',
      name: 'StandaloneCouponRedeemScan',
      component: () => import('@/views/standalone/CouponRedeemScanView.vue'),
      meta: { standalone: true }
    },
    {
      path: '/standalone/coupon-redeem/:publicKey',
      name: 'StandaloneCouponRedeem',
      component: () => import('@/views/standalone/CouponRedeemView.vue'),
      meta: { standalone: true }
    },
    // Public enrollment page — NO login. Mirrors the forms-engine-frontend
    // `/enrollment/:token` URL and embeds that flow in an iframe so the
    // submission can be captured here for payment handling.
    {
      path: '/enrollment/:token',
      name: 'EnrollmentEmbed',
      component: () => import('@/views/EnrollmentEmbedView.vue'),
      meta: { standalone: true }
    },
    // Development only route for testing
    ...(MODE_ENV === 'development'
      ? [
          {
            path: '/dev-test',
            name: 'DevTest',
            component: () => import('@/views/LobbyLandingView.vue'),
            beforeEnter: auth
          }
        ]
      : [])
  ]
})

/**
 * Global guard: intercept cloud_token from eStore cross-app SSO.
 * Exchanges the Sanctum Bearer token for a session cookie, then strips
 * the token from the URL before the per-route guards run.
 */
router.beforeEach(async to => {
  if (to.meta?.standalone) return true

  const cloud_token = to.query.cloud_token as string | undefined
  if (!cloud_token) return true

  localStorage.setItem('auth_token', cloud_token)

  try {
    await API.Get.csrf()
    await API.Post.exchangeToken()
  } catch (error) {
    console.error('Cloud token exchange failed:', error)
    localStorage.removeItem('auth_token')
    window.location.href = LOBBY_URL
    return false
  }

  const { cloud_token: _stripped, ...remaining_query } = to.query
  return { path: to.path, query: remaining_query, replace: true }
})

/**
 * Validates if a given path corresponds to a valid protected route
 * Used by landing pages to determine if a target route is valid for redirection
 *
 * @param targetPath - The path to validate (e.g., '/dashboard', '/receipt/123')
 * @returns boolean - true if the path is a valid route
 */
export const isValidProtectedRoute = (targetPath: string): boolean => {
  // Remove leading slash for consistency
  const normalizedPath = targetPath.startsWith('/') ? targetPath.slice(1) : targetPath

  // If empty or just '/', default to dashboard (which is valid)
  if (!normalizedPath || normalizedPath === '') {
    return true
  }

  // Exclude landing pages - they're not valid targets for post-auth redirection
  if (normalizedPath.startsWith('landing-')) {
    return false
  }

  try {
    // Use Vue Router's resolve method to check if the route exists
    const resolved = router.resolve(targetPath)

    // Check if route was resolved successfully and uses the initialize middleware
    // (meaning it's a protected route that requires authentication)
    return (
      resolved.name !== undefined &&
      resolved.matched.length > 0 &&
      !resolved.name?.toString().toLowerCase().includes('landing')
    )
  } catch (error) {
    // If router.resolve throws an error, the route is invalid
    return false
  }
}

export default router
